import { useIxcRegistrationV1ProfileQueryServiceProfileList } from '@interxfi/api-client/generated/hooks/ixc-registration-v1-profile-query-service-profile-list';
import type { RangeFilter } from '@interxfi/table-config/filter';
import {
  IxcRegistrationV1ProfileQueryServiceProfileListFilterableFields,
  type IxcRegistrationV1ProfileQueryServiceProfileListProfileListRequest,
  IxcRegistrationV1ProfileQueryServiceProfileListSearchableFields,
  type IxcRegistrationV1ProfileState,
  type IxcRegistrationV1ProfileStatus,
  type IxcRegistrationV1ProfileTypeOneOfValue,
} from '@interxfi/types/generated/api';
import { getProfileName } from '../../../features/registration/profile/profile-utils';
import { createDataCombobox } from '../data-combobox-builder';

export function buildProfileListQuery(filters: {
  programId?: string;
  status?: IxcRegistrationV1ProfileStatus;
  kind?: IxcRegistrationV1ProfileTypeOneOfValue | IxcRegistrationV1ProfileTypeOneOfValue[];
  w9Date?: string | RangeFilter;
}): IxcRegistrationV1ProfileQueryServiceProfileListProfileListRequest | undefined {
  const query: IxcRegistrationV1ProfileQueryServiceProfileListProfileListRequest['query'] = {};
  const queryFilters: Exclude<IxcRegistrationV1ProfileQueryServiceProfileListProfileListRequest['query'], undefined>['filters'] = [];

  if (filters.programId) {
    queryFilters.push({
      field: {
        name: IxcRegistrationV1ProfileQueryServiceProfileListFilterableFields.ProgramId,
        type: { value: filters.programId },
      },
    });
  }

  if (filters.kind) {
    queryFilters.push({
      field: {
        name: IxcRegistrationV1ProfileQueryServiceProfileListFilterableFields.DataCurrentVersionProfileType,
        type: Array.isArray(filters.kind) ? { in: { values: filters.kind } } : { value: filters.kind },
      },
    });
  }

  if (filters.status) {
    queryFilters.push({
      field: {
        name: IxcRegistrationV1ProfileQueryServiceProfileListFilterableFields.Status,
        type: { value: filters.status },
      },
    });
  }

  if (filters.w9Date) {
    const w9DateFilters: Record<IxcRegistrationV1ProfileTypeOneOfValue, (typeof queryFilters)[number]> = {
      individual: {
        field: {
          name: IxcRegistrationV1ProfileQueryServiceProfileListFilterableFields.DataCurrentVersionProfileIndividualW9Signed,
          type: typeof filters.w9Date === 'string' ? { value: filters.w9Date } : { range: filters.w9Date },
        },
      },
      organization: {
        field: {
          name: IxcRegistrationV1ProfileQueryServiceProfileListFilterableFields.DataCurrentVersionProfileOrganizationW9Signed,
          type: typeof filters.w9Date === 'string' ? { value: filters.w9Date } : { range: filters.w9Date },
        },
      },
    };

    const allFilters = Object.values(w9DateFilters);

    if (!filters.kind) {
      queryFilters.push({
        or: { filters: allFilters },
      });
    }

    const allowedKindsAsArray = (Array.isArray(filters.kind) ? filters.kind : [filters.kind]) as IxcRegistrationV1ProfileTypeOneOfValue[];
    const filteredFilters = allowedKindsAsArray.map((k) => w9DateFilters[k]);

    if (filteredFilters.length) {
      queryFilters.push(
        filteredFilters.length === 1
          ? filteredFilters[0]
          : {
              or: { filters: filteredFilters },
            },
      );
    }
  }

  if (queryFilters.length) {
    query.filters = queryFilters;
  }

  return Object.values(query).some(Boolean) ? { query } : {};
}

export function profileOptionDisplayBuilder(profile: IxcRegistrationV1ProfileState) {
  return {
    label: getProfileName(profile) || profile.profileId,
  };
}

export const {
  DataComboboxInput: RegistrationProfileCombobox,
  DataComboboxInputWithLens: RegistrationProfileComboboxWithLens,
  DataMultiComboboxInput: RegistrationProfileMultiCombobox,
  DataMultiComboboxInputWithLens: RegistrationProfileMultiComboboxWithLens,
} = createDataCombobox<typeof useIxcRegistrationV1ProfileQueryServiceProfileList, 'profile', 'profileId'>({
  dataValueKey: 'profileId',
  entityName: 'profiles',
  responseDataKey: 'profile',
  searchFields: [
    IxcRegistrationV1ProfileQueryServiceProfileListSearchableFields.DataCurrentVersionProfileIndividualNameFirst,
    IxcRegistrationV1ProfileQueryServiceProfileListSearchableFields.DataCurrentVersionProfileIndividualNameLast,
    IxcRegistrationV1ProfileQueryServiceProfileListSearchableFields.DataCurrentVersionProfileOrganizationLegalName,
    IxcRegistrationV1ProfileQueryServiceProfileListSearchableFields.DataCurrentVersionProfileOrganizationDbaName,
    IxcRegistrationV1ProfileQueryServiceProfileListSearchableFields.DataPendingVersionProfileIndividualNameFirst,
    IxcRegistrationV1ProfileQueryServiceProfileListSearchableFields.DataPendingVersionProfileIndividualNameLast,
    IxcRegistrationV1ProfileQueryServiceProfileListSearchableFields.DataPendingVersionProfileOrganizationLegalName,
    IxcRegistrationV1ProfileQueryServiceProfileListSearchableFields.DataPendingVersionProfileOrganizationDbaName,
  ],
  useInfiniteQuery: useIxcRegistrationV1ProfileQueryServiceProfileList,
  optionDisplayBuilder: profileOptionDisplayBuilder,
});
